import * as R from "ramda";
import React from "react";

import { Button, Form, Input, Radio, Select, Switch } from "antd";
import { css } from "glamor";

import { countryIsoCodeToName } from "../../formatters";
import NullableCheckboxFormItem from "./NullableCheckboxFormItem";

const styles = {
    formSection: css({
        marginBottom: "1rem",
        " .ant-form-item": {
            marginBottom: ".5rem",
        },
    }),
};

const getOptionsFromFacets = R.curryN(
    2,
    (
        facets,
        fieldName,
        opts = {
            includeAll: false,
        }
    ) => {
        let options = R.pipe(
            R.find(R.propEq("fieldName", fieldName)),
            R.propOr([], "values"),
            R.reject(R.propEq("label", "")),
            R.reject(R.pipe(R.prop("value"), R.startsWith("_")))
        )(facets);

        if (opts.includeAll) {
            options = [
                { key: "all", label: "All", value: undefined },
                ...options,
            ];
        }

        return options;
    }
);

const filterOption = (input, option) => {
    return (
        option.props.children.toLowerCase().indexOf(input.toLowerCase()) > -1
    );
};

const getSelectProps = () => ({
    filterOption,
    optionFilterProp: "value",
    showSearch: true,
});

const AdvancedSearchForm = ({
    onFilterChange,
    onClearFilters,
    filters = {},
    facets = [],
}) => {
    const getOptions = getOptionsFromFacets(facets);

    return (
        <Form layout="vertical">
            <section {...styles.formSection}>
                <h3>Advanced Search</h3>
                {/* AR-11281 removed launch darkly "T9357-alias-names-query" */}
                <Form.Item>
                    <Switch
                        checkedChildren="Search in local names"
                        unCheckedChildren="Search in main name only"
                        onChange={onFilterChange("lookupAlternativeNames")}
                        value={filters.lookupAlternativeNames}
                    />
                </Form.Item>

                <NullableCheckboxFormItem
                    label="Type"
                    options={getOptions("entityTypes")}
                    onChange={onFilterChange("entityType")}
                    value={filters.entityType}
                />

                <Form.Item label="Country">
                    <Select
                        {...getSelectProps()}
                        mode="multiple"
                        placeholder="Select countries"
                        onChange={onFilterChange("countries")}
                        value={filters.countries}
                    >
                        {getOptions("countries").map((o, i) => (
                            <Select.Option key={i} value={o.value}>
                                {countryIsoCodeToName(o.label)}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label="State">
                    <Select
                        {...getSelectProps()}
                        placeholder="Select state"
                        onChange={onFilterChange("state")}
                        value={filters.state}
                    >
                        {getOptions("states", {
                            includeAll: true,
                        }).map((o, i) => (
                            <Select.Option key={i} value={o.value}>
                                {o.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <NullableCheckboxFormItem
                    label="Status"
                    options={getOptions("registrationStatuses")}
                    onChange={onFilterChange("registrationStatus")}
                    value={filters.registrationStatus}
                />

                <Form.Item label="Line of Business">
                    <Select
                        {...getSelectProps()}
                        placeholder="Select line of business"
                        onChange={onFilterChange("linesOfBusiness")}
                        value={filters.linesOfBusiness}
                    >
                        {getOptions("linesOfBusiness", {
                            includeAll: true,
                        }).map((o, i) => (
                            <Select.Option key={i} value={o.value}>
                                {o.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            </section>

            <section {...styles.formSection}>
                <h3>Search by further entity attributes</h3>

                <NullableCheckboxFormItem
                    label="Risk"
                    options={getOptions("riskLevels")}
                    onChange={onFilterChange("risks")}
                    value={filters.risks}
                />

                <Form.Item label="Entity Source">
                    <Input
                        placeholder="Search entity source"
                        onChange={onFilterChange("entitySource")}
                        value={filters.entitySource}
                    />
                </Form.Item>
            </section>

            <section {...styles.formSection}>
                <h3>Search by policy attributes</h3>

                <Form.Item label="Screening Level">
                    <Radio.Group
                        options={getOptions("monitoringLevels", {
                            includeAll: true,
                        })}
                        onChange={onFilterChange("monitoringLevel")}
                        value={filters.monitoringLevel}
                    />
                </Form.Item>

                <Form.Item label="Frequency">
                    <Radio.Group
                        options={getOptions("frequencies", {
                            includeAll: true,
                        })}
                        onChange={onFilterChange("frequency")}
                        value={filters.frequency}
                    />
                </Form.Item>
            </section>

            <section {...styles.formSection}>
                <h3>Search by ID</h3>

                <Form.Item label="Identification Number">
                    <Input
                        placeholder="Search identification number"
                        onChange={onFilterChange("identificationNumber")}
                        value={filters.identificationNumber}
                    />
                </Form.Item>

                <Form.Item label="External ID">
                    <Input
                        placeholder="Search external ID"
                        onChange={onFilterChange("externalId")}
                        value={filters.externalId}
                    />
                </Form.Item>

                <Form.Item label="Arachnys ID">
                    <Input
                        placeholder="Search Arachnys ID"
                        onChange={onFilterChange("ulid")}
                        value={filters.ulid}
                    />
                </Form.Item>
            </section>

            <section {...styles.formSection}>
                <Form.Item>
                    <Button onClick={onClearFilters}>Clear</Button>
                </Form.Item>
            </section>
        </Form>
    );
};

export default AdvancedSearchForm;
